import { Elements } from '@/components/elements';
import featuresBackground from '@images/hero-bg.webp';
import { useMemo } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import {
    getStateDisplayNameFromCode,
    parseShopPlansPath,
    titleCaseFromSlug,
} from '@/lib/shopPlansRouting';

function ensureCountySuffix(countyDisplay: string) {
    const s = (countyDisplay || '').trim();
    if (!s) return '';
    // Avoid double-suffix if the source already includes it (or similar).
    const lower = s.toLowerCase();
    if (lower.includes('county')) return s;
    return `${s} County`;
}

export const PlansHero = () => {
    const [searchParams] = useSearchParams();
    const location = useLocation();

    const title = useMemo(() => {
        const parsedPath = parseShopPlansPath(location.pathname);
        let stateAbbr = '';
        let countyRaw = '';

        if (parsedPath.kind === 'state' || parsedPath.kind === 'state-county') {
            stateAbbr = String(parsedPath.stateCode || '').toUpperCase();
            countyRaw = parsedPath.kind === 'state-county' ? String(parsedPath.countySlug || '') : '';
        } else {
            const stateRaw = String(searchParams.get('state') || '');
            countyRaw = String(searchParams.get('county') || '');
            if (stateRaw) {
                const stateDecoded = decodeURIComponent(stateRaw);
                const match = stateDecoded.match(/^([A-Z]{2})-/);
                stateAbbr = String(match?.[1] || stateDecoded).toUpperCase();
            }
        }

        if (!stateAbbr) return 'Shop Individual Health Plans';

        const stateFullName = getStateDisplayNameFromCode(stateAbbr);

        if (!countyRaw) {
            return `Individual Health Plans In ${stateFullName} (${stateAbbr})`;
        }

        const countyDisplay = ensureCountySuffix(titleCaseFromSlug(countyRaw));
        return `Individual Health Plans In ${countyDisplay}, ${stateFullName} (${stateAbbr})`;
    }, [location.pathname, searchParams]);

    return (
        <>
            <section
                className="flex max-h-[60px] overflow-hidden bg-cover bg-top bg-no-repeat px-5 pt-20 pb-25 md:px-10 lg:px-[79px]"
                style={{ backgroundImage: `url(${featuresBackground})` }}
            >
                <div className="mx-auto flex w-full max-w-[1200px] flex-col items-center justify-center gap-[30px]">
                    <div className="w-full">
                        <div className="mt-0 flex w-full flex-col gap-2 items-center lg:mt-8">
                            <Elements.Title
                                title={title}
                                type="h1"
                                className="text-center text-3xl leading-tight font-semibold text-gray-50 md:text-4xl md:leading-[54px] lg:text-5xl lg:leading-[58px]"
                            />
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};
